/**
 * 统一的认证工具类 - 整合简单版和完整版功能
 * 基于数据库表结构设计，支持多种使用场景
 */

const STORAGE_KEYS = {
  TOKEN: 'xinjie_admin_token',
  USER: 'xinjie_admin_user', 
  EXPIRE: 'xinjie_admin_expire',
  // 兼容简单版的key
  TOKEN_SIMPLE: 'token',
  USER_SIMPLE: 'userInfo',
  EXPIRE_SIMPLE: 'tokenExpiry'
};

class AuthService {
  /**
   * 保存认证信息 - 兼容多种数据格式
   */
  static saveAuth(authData) {
    try {
      // 兼容不同的数据结构
      const token = authData.token;
      const user = authData.user || authData.admin;
      
      if (!token || !user) {
        throw new Error('认证数据不完整');
      }

      // 保存到新格式（过期时间与 JWT exp 对齐，默认 7 天）
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      let expireTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp) {
          expireTime = payload.exp * 1000;
        }
      } catch (_) {
        // 解析失败时使用默认 7 天
      }
      localStorage.setItem(STORAGE_KEYS.EXPIRE, expireTime.toString());
      
      // 同时保存到简单版格式，保持兼容性
      localStorage.setItem(STORAGE_KEYS.TOKEN_SIMPLE, token);
      localStorage.setItem(STORAGE_KEYS.USER_SIMPLE, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.EXPIRE_SIMPLE, expireTime.toString());
      
      console.log('✅ 认证信息保存成功:', { userId: user.id, username: user.username });
    } catch (error) {
      console.error('❌ 保存认证信息失败:', error);
      throw error;
    }
  }

  /**
   * 获取token - 优先新格式，兼容旧格式
   */
  static getToken() {
    return localStorage.getItem(STORAGE_KEYS.TOKEN) || 
           localStorage.getItem(STORAGE_KEYS.TOKEN_SIMPLE);
  }

  /**
   * 获取用户信息 - 优先新格式，兼容旧格式
   */
  static getUserInfo() {
    try {
      let userStr = localStorage.getItem(STORAGE_KEYS.USER);
      if (!userStr) {
        userStr = localStorage.getItem(STORAGE_KEYS.USER_SIMPLE);
      }
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return null;
    }
  }

  static getPermissions() {
    const user = this.getUserInfo();
    const permissions = user?.permissions || [];
    return Array.isArray(permissions) ? permissions : [];
  }

  static hasPermission(permission) {
    if (!permission) return true;
    const user = this.getUserInfo();
    if (user?.isSuperAdmin || user?.role?.code === 'super_admin') return true;
    const permissions = this.getPermissions();
    return permissions.includes('*') || permissions.includes(permission);
  }

  static hasAnyPermission(permissions = []) {
    return permissions.length === 0 || permissions.some(permission =>
      this.hasPermission(permission)
    );
  }

  /**
   * 检查是否已认证 - 兼容多种格式
   */
  static isAuthenticated() {
    const token = this.getToken();
    let expireTime = localStorage.getItem(STORAGE_KEYS.EXPIRE);
    
    // 兼容简单版过期时间
    if (!expireTime) {
      expireTime = localStorage.getItem(STORAGE_KEYS.EXPIRE_SIMPLE);
    }
    
    if (!token || !expireTime) {
      return false;
    }
    
    // 检查是否过期
    if (Date.now() > parseInt(expireTime)) {
      console.log('Token已过期，清除认证信息');
      this.clearAuth();
      return false;
    }
    
    return true;
  }

  /**
   * 清除认证信息 - 清除所有格式的数据
   */
  static clearAuth() {
    // 清除新格式
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.EXPIRE);
    
    // 清除简单版格式
    localStorage.removeItem(STORAGE_KEYS.TOKEN_SIMPLE);
    localStorage.removeItem(STORAGE_KEYS.USER_SIMPLE);
    localStorage.removeItem(STORAGE_KEYS.EXPIRE_SIMPLE);
    
    console.log('🧹 认证信息已清除');
  }

  /**
   * 登出
   */
  static async logout() {
    try {
      const token = this.getToken();
      
      // 通知服务器登出
      if (token) {
        try {
          const { logoutAdmin } = await import('../api/auth');
          await logoutAdmin();
        } catch (error) {
          console.warn('服务器登出失败:', error.message);
        }
      }
      
      // 清除本地认证信息
      this.clearAuth();
      
      return { success: true };
    } catch (error) {
      console.error('登出失败:', error);
      // 即使出错也要清除本地信息
      this.clearAuth();
      return { success: false, message: error.message };
    }
  }

  /**
   * 处理认证失败
   */
  static handleAuthFailure() {
    console.log('🚨 处理认证失败');
    this.clearAuth();
    
    // 跳转到登录页
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  /**
   * 获取认证头
   */
  static getAuthHeader() {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }

  // 兼容性方法（空实现，避免旧代码报错）
  static resetActivityMonitor() {}
  static async handleTokenExpiry() { return this.isAuthenticated(); }
  static initActivityMonitor() {}
  static stopHeartbeat() {}
  
  // 简单版兼容方法
  static isTokenExpiringSoon() {
    const expireTime = localStorage.getItem(STORAGE_KEYS.EXPIRE) || 
                      localStorage.getItem(STORAGE_KEYS.EXPIRE_SIMPLE);
    if (!expireTime) return true;
    
    const timeUntilExpiry = parseInt(expireTime) - Date.now();
    const oneHour = 60 * 60 * 1000; // 1小时
    
    return timeUntilExpiry < oneHour;
  }
  
  static extendTokenExpiry() {
    try {
      const newExpiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7天
      localStorage.setItem(STORAGE_KEYS.EXPIRE, newExpiryTime.toString());
      localStorage.setItem(STORAGE_KEYS.EXPIRE_SIMPLE, newExpiryTime.toString());
      console.log('Token有效期已延长');
    } catch (error) {
      console.error('延长token有效期失败:', error);
    }
  }
}

export default AuthService;